import * as React from "react";
import { observer } from "mobx-react-lite";
import { mk8sDryRun } from "./../../../../../mobxStores/dryRun/mk8s";
import { Mk8sDraftMobx } from "./../../../../../mst/stores/mk8s.draft";
import { homeLink, request } from "./../../../../../services/cpln";
import { DryRunAlert } from "./../../../dryRunAlert";
import { MoreHorizontal, PlusCircle } from "react-feather";
import { NGKindSelect } from "./../../../../../newcomponents/select/ngkindselect";
import { useNGFormContext } from "../../../../../reactContexts/ngFormContext";
import { NGFormElement } from "../../../../../newcomponents/ngformelement/ngformelement";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { NGFormLabel } from "../../../../../newcomponents/text/formLabel";
import { NGProviderCreateOpaqueSecret } from "../../../ngProviderCreateOpaqueSecret";
import NGAlert from "../../../../../newcomponents/alert";
import { getDryRunErrorText } from "../../../getDryRunErrorText";
import { NGInputListMst } from "../../../../../newcomponents/inputList/inputListMst";
import { NGInput } from "../../../../../newcomponents/input/input";
import { NGButton } from "../../../../../newcomponents/button/Button";

interface Props {
  mk8sDraft: Mk8sDraftMobx;
}
const Mk8sCreateProviderDigitalOceanRaw: React.FC<Props> = ({ mk8sDraft }) => {
  const d = mk8sDraft.provider_digitalocean!;
  const formData = useNGFormContext();

  const [browserKey, setBrowserKey] = React.useState("");

  const sshKeysDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.digitalocean.sshKeys`,
    paths: d.sshKeys.items.map((_, index) => `spec.provider.digitalocean.sshKeys[${index}]`),
  });

  return (
    <>
      <div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={"digitalocean.region"}
            label={d.region.label}
            value={d.region.value}
            onChange={d.region.setValue}
            required={d.region.isRequired}
            error={d.region.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => {
                  setBrowserKey("regions");
                  formData.set("digitalocean.region", { touched: true });
                },
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("digitalocean.region").touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={d.region.setValue}
              path={"spec.provider.digitalocean.region"}
            />
          </div>
        </div>

        <div className="mb-4">
          <NGFormLabel required invalid={!d.tokenSecretName}>
            Digital Ocean Token Secret
          </NGFormLabel>
          <div className="mb-2 flex items-start gap-2">
            <NGKindSelect
              name={"digitalocean.tokenSecretName"}
              style={{ width: 450 }}
              value={d.tokenSecretName}
              invalid={!d.tokenSecretName}
              onChange={(value) => d.setTokenSecretName(value)}
              kind={"secret"}
              queries={[{ property: "type", value: "opaque" }]}
              buttons={[
                {
                  title: "Create",
                  render: () => <PlusCircle className="h-4" />,
                  onClick: () => setBrowserKey("createSecret"),
                },
              ]}
            />
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("digitalocean.tokenSecretName").touched}
              dryRunResponse={mk8sDryRun.response}
              path={"spec.provider.digitalocean.tokenSecretLink"}
            />
          </div>
          <NGAlert
            style={{ width: 450 }}
            render={() => (
              <div className="flex flex-col text-sm">
                <span>An opaque secret that contains your Digital Ocean token.</span>
                <span>It will be used to manage servers in your Digital Ocean account.</span>
              </div>
            )}
          />
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={"digitalocean.vpcId"}
            label={d.vpcId.label}
            value={d.vpcId.value}
            onChange={d.vpcId.setValue}
            required={d.vpcId.isRequired}
            error={d.vpcId.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => {
                  setBrowserKey("vpcId");
                  formData.set("digitalocean.vpcId", { touched: true });
                },
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("digitalocean.vpcId").touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={d.vpcId.setValue}
              path={"spec.provider.digitalocean.vpcId"}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={"digitalocean.image"}
            label={d.image.label}
            value={d.image.value}
            onChange={d.image.setValue}
            required={d.image.isRequired}
            error={d.image.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => {
                  setBrowserKey("image");
                  formData.set("digitalocean.image", { touched: true });
                },
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("digitalocean.image").touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={d.image.setValue}
              path={"spec.provider.digitalocean.image"}
            />
          </div>
        </div>

        <NGInputListMst
          data={d.sshKeys}
          required={true}
          className="mb-8"
          styles={{ container: { width: 450, minWidth: 450 } }}
          label="Digital SSH Keys"
          labelRender={() => {
            return (
              <>
                <NGButton
                  className="ml-1"
                  size={"small"}
                  variant={"secondary"}
                  onClick={() => setBrowserKey("ssh-keys")}
                >
                  Browse
                </NGButton>
              </>
            );
          }}
          firstInput={(i, index) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && sshKeysDryRun.message.includes(`[${index}]`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          invalid={d.sshKeys.items.length < 1 ? true : sshKeysDryRun.severity === "error"}
          error={
            d.sshKeys.items.length < 1
              ? "At least 1 item is required."
              : sshKeysDryRun.severity === "error"
              ? sshKeysDryRun.message
              : ""
          }
          warning={sshKeysDryRun.severity === "error" ? "" : sshKeysDryRun.message}
        />
      </div>

      {browserKey === "createSecret" ? (
        <NGProviderCreateOpaqueSecret
          onOk={(secretName: string) => d.setTokenSecretName(secretName)}
          onClose={() => setBrowserKey("")}
          title={"Create Digital Ocean Token Secret"}
          dataLabel={"Digital Ocean Token"}
        />
      ) : null}
      {browserKey === "regions" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean Regions"
          initialValue={d.region.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "regions",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.regions;
          }}
          onOk={(value) => d.region.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Slug"]}
          getData={(item: any) => [item.slug, item.name, item.slug]}
        />
      ) : null}
      {browserKey === "vpcId" ? (
        <NGProviderBrowser
          key={d.tokenSecretName}
          title="Browse Digital Ocean VPCs"
          initialValue={d.vpcId.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "vpcs",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.vpcs;
          }}
          onOk={(value) => d.vpcId.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name", "ID", "Description", "IP Range"]}
          getData={(item: any) => [String(item.id), item.name, String(item.id), item.description, item.ip_range]}
        />
      ) : null}
      {browserKey === "image" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean Images"
          initialValue={d.image.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "images",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.images.sort((a: any, b: any) => {
              if (a.slug > b.slug) {
                return 1;
              }
              if (a.slug < b.slug) {
                return -1;
              }
              return 0;
            });
          }}
          onOk={(value) => d.image.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Availability", "Slug"]}
          tooltips={[
            {
              label: "Availability",
              content: `Shows if the image is available in selected region (${mk8sDraft.provider_digitalocean.region.value})`,
            },
          ]}
          getData={(item: any) => {
            const available = item.regions.includes(mk8sDraft.provider_digitalocean.region.value);

            return [item.slug, item.description, [available, available ? "Available" : "Not Available"], item.slug];
          }}
        />
      ) : null}
      {browserKey === "ssh-keys" ? (
        <NGProviderBrowser
          key={d.tokenSecretName}
          title="Browse Digital Ocean SSH Keys"
          initialValue={d.sshKeys.items.map((i) => i.firstValue)}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "ssh_keys",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.ssh_keys;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            for (const value of values) {
              if (d.sshKeys.items.some((i) => i.firstValue === value)) {
                continue;
              }
              d.sshKeys.add();
              const i = d.sshKeys.items[d.sshKeys.items.length - 1];
              i.setFirstValue(value);
            }
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "ID"]}
          getData={(item: any) => [String(item.id), item.name, String(item.id)]}
        />
      ) : null}
    </>
  );
};

export const Mk8sCreateProviderDigitalOcean = observer(Mk8sCreateProviderDigitalOceanRaw);
